import React from "react";
import "./Privacy.css";
import Footer from "../../components/Footer/Footer";

export default function Privacy({ navigate, showComingSoon }) {
  return (
    <>
      {/* Hero */}
      <div className="privacy-hero">
        <div className="privacy-hero-inner">
          <div className="big-icon">🔒</div>
          <h1>Privacy Policy</h1>
          <p>
            We collect only what we need and protect everything you share. Your
            data belongs to you.
          </p>
        </div>
      </div>

      {/* Trust pillars */}
      <div className="trust-pillars">
        <div className="tp-inner">
          <div className="tp-card">
            <div className="tp-icon">🚫</div>
            <h3>We Never Sell Your Data</h3>
            <p>
              Your location history, activity records, and conversations are
              never sold to third parties, advertisers, or data brokers. Full
              stop.
            </p>
          </div>
          <div className="tp-card">
            <div className="tp-icon">🔐</div>
            <h3>Encrypted Storage</h3>
            <p>
              All data at rest and in transit is encrypted using
              industry-standard protocols. Your logs and conversations are
              protected by default.
            </p>
          </div>
          <div className="tp-card">
            <div className="tp-icon">🗑️</div>
            <h3>You Control Your Data</h3>
            <p>
              Delete your account and all associated data at any time from
              within the app. No hidden retention, no questions asked.
            </p>
          </div>
        </div>
      </div>

      {/* Full policy text */}
      <div className="legal-body">
        <div className="legal-inner">
          <span className="legal-date">Last updated: January 1, 2025</span>

          <h2>1. Introduction</h2>
          <p>
            RoadDogg AI is a product of <strong>Kustom Habitz LLC</strong>{" "}
            ("we," "our," or "us"). Kustom Habitz LLC is committed to protecting
            your privacy. This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our mobile
            application ("App") and related services.
          </p>
          <p>
            By using RoadDogg AI, you agree to the collection and use of
            information in accordance with this policy. If you do not agree with
            the terms of this policy, please do not use the App.
          </p>

          <h2>2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <ul>
            <li>
              <strong>Account Information:</strong> Name, email address, and
              account credentials when you register.
            </li>
            <li>
              <strong>Location Data:</strong> GPS coordinates and location
              history when you use the Maps feature. Location is only collected
              while the App is actively in use unless you grant background
              location permission.
            </li>
            <li>
              <strong>Usage Data:</strong> Information about how you use the
              App, including features accessed, session duration, and crash
              reports.
            </li>
            <li>
              <strong>User-Generated Content:</strong> Activity logs, expense
              records, maintenance notes, receipts, and other content you create
              within the App.
            </li>
            <li>
              <strong>Device Information:</strong> Device type, operating system
              version, unique device identifiers, and mobile network
              information.
            </li>
            <li>
              <strong>AI Conversation Data:</strong> Text and voice input
              provided to the AI assistant for the purpose of generating
              responses. This data is processed to provide the service and is
              not stored permanently beyond your active session unless you save
              a log.
            </li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the App and its features</li>
            <li>Process and respond to your AI assistant queries</li>
            <li>
              Display relevant maps, weather, and location-based information
            </li>
            <li>Store and organize your driving logs and records</li>
            <li>Send important service notifications and app updates</li>
            <li>
              Analyze usage patterns to improve the App (aggregated, anonymized
              data only)
            </li>
            <li>
              Detect, prevent, and address technical issues and security threats
            </li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. Data Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or rent your personal information to third
            parties. We may share your data in the following limited
            circumstances:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> With trusted third-party
              vendors (such as Firebase for cloud storage, Google Maps API for
              mapping, and AI providers for assistant functionality) who assist
              in operating our App under strict confidentiality agreements.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law, court
              order, or governmental authority.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of all or a portion of our assets, with
              appropriate notice to you.
            </li>
            <li>
              <strong>Safety:</strong> To protect the rights, property, or
              safety of RoadDogg AI, our users, or others.
            </li>
          </ul>

          <h2>5. Location Data</h2>
          <p>
            RoadDogg AI uses your location to power the Maps feature, provide
            nearby places, points of interest, and location-aware services, and
            deliver location-aware weather information. We request location
            access only when you actively use these features. You may disable
            location access at any time through your device settings, though
            this will limit certain functionality.
          </p>

          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. This includes encryption of
            data in transit (TLS/SSL) and at rest, access controls, and regular
            security assessments. However, no method of transmission over the
            internet or electronic storage is 100% secure.
          </p>

          <h2>7. App Lock &amp; Privacy Features</h2>
          <p>
            RoadDogg AI includes a built-in App Lock feature that requires
            biometric or passcode authentication before granting access to your
            data. We strongly recommend enabling this feature to protect your
            activity records and conversation history from unauthorized access
            on your device.
          </p>

          {/* change #4 — voice/audio privacy clarification */}
          <h2>8. Microphone &amp; Voice Data</h2>
          <p>
            Microphone access is only used while you are actively interacting
            with the voice assistant features. Audio is temporarily processed to
            generate AI responses and is not permanently stored unless
            explicitly saved by you. We do not use voice recordings for
            advertising, profiling, or any purpose beyond delivering the
            in-session assistant response.
          </p>

          <h2>9. Data Retention</h2>
          <p>
            We retain your personal data for as long as your account is active
            or as needed to provide the service. You may delete specific logs or
            your entire account at any time from within the App. Upon account
            deletion, your personal data is permanently removed from our systems
            within 30 days, except where retention is required by law.
          </p>

          <h2>10. Children's Privacy</h2>
          <p>
            RoadDogg AI is not directed to individuals under the age of 18. We
            do not knowingly collect personal information from children. If you
            believe a child has provided us with personal information, please
            contact us immediately.
          </p>

          <h2>11. Your Rights</h2>
          <p>
            Depending on your location, you may have certain rights regarding
            your personal data, including:
          </p>
          <ul>
            <li>The right to access the personal data we hold about you</li>
            <li>The right to correct inaccurate or incomplete data</li>
            <li>
              The right to delete your personal data ("right to be forgotten")
            </li>
            <li>The right to restrict or object to processing of your data</li>
            <li>The right to data portability</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at the address below.
          </p>

          <h2>12. Third-Party Services</h2>
          <p>
            Our App integrates with third-party services including Google Maps,
            Firebase (Google), and AI processing providers. These services have
            their own privacy policies governing their use of your data. We
            encourage you to review their policies.
          </p>

          <h2>13. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of significant changes by posting a notice within the App or
            sending an email to your registered address. Continued use of the
            App after changes constitutes acceptance of the updated policy.
          </p>

          <h2>14. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us at:
          </p>
          <p>
            <strong>Kustom Habitz LLC</strong> — Developer &amp; Operator of
            RoadDogg AI
            <br />
            Email:{" "}
            <a href="mailto:kustomhabitzz@gmail.com">kustomhabitzz@gmail.com</a>
            <br />
            Website:{" "}
            <a
              onClick={() => navigate("support")}
              style={{ cursor: "pointer" }}
            >
              roaddoggai.com/support
            </a>
          </p>
        </div>
      </div>

      <Footer navigate={navigate} showComingSoon={showComingSoon} />
    </>
  );
}
